import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";

import { listGateways } from "@/features/agentGateway/api";
import { agentGatewayKeys } from "@/features/agentGateway/hooks";
import { useConnectors, useOrg, usePolicies, useSignIn } from "@/features/privateAccess/hooks";
import { useCapabilities } from "@/lib/capabilities";
import { useAuthStore, type AuthenticatedUser } from "@/stores/authStore";

import { getAccount, setProductView } from "./api";
import { buildChecklist, type ChecklistInput } from "./checklist";
import type { SaseOverview } from "./overview/types";
import { isProductView, type ChecklistItem, type ProductView } from "./types";

export const saseKeys = {
  account: () => ["sase", "account"] as const,
};

/** The SASE section and the two modules it shares with the cloud (SD-01). */
export function useSaseAvailability() {
  const capabilities = useCapabilities();
  const enabled = Boolean(capabilities.sase?.enabled);
  return {
    enabled,
    privateAccess: enabled && Boolean(capabilities.sase?.modules.private_access),
    agentGateways: enabled && Boolean(capabilities.sase?.modules.agent_gateways),
  };
}

/**
 * The account's product view. The login answer carries it; while the SASE
 * section exists here it is also re-read from the account, so a change made
 * in another browser applies on the next load. Anything unknown reads as
 * "everything". The SASE edition (SASE-30) has no cloud sections, so there it
 * is always "SASE only", whatever the account says.
 */
export function useProductView(saseEnabled: boolean): ProductView {
  const saseEdition = useSaseEdition();
  const signedIn = useAuthStore((s) => Boolean(s.user));
  const stored = useAuthStore((s) => s.user?.product_view);
  const account = useQuery({
    queryKey: saseKeys.account(),
    queryFn: () => getAccount(),
    enabled: signedIn && saseEnabled && !saseEdition,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  if (saseEdition) return "sase_only";
  const view = account.data?.product_view ?? stored;
  return isProductView(view) ? view : "everything";
}

/** The standalone SASE edition (`capabilities.sase.edition`, SASE-30). */
export function useSaseEdition(): boolean {
  return useCapabilities().sase?.edition === "sase";
}

export function useSetProductView(): UseMutationResult<AuthenticatedUser, Error, ProductView> {
  const queryClient = useQueryClient();
  const updateUser = useAuthStore((s) => s.updateUser);
  return useMutation({
    mutationFn: (view: ProductView) => setProductView(view),
    onSuccess: (account) => {
      queryClient.setQueryData(saseKeys.account(), account);
      const current = useAuthStore.getState().user;
      if (current) updateUser({ ...current, product_view: account.product_view });
    },
  });
}

/** The Overview's setup checklist, read from what exists on this cloud:
 *  Private access's own reads plus the SASE overview read (C2), which the
 *  page fetches once for its tiles and passes in. */
export function useSetupChecklist(
  overview: SaseOverview | null = null,
  overviewLoading = false,
): { items: ChecklistItem[]; loading: boolean } {
  const capabilities = useCapabilities();
  const { privateAccess } = useSaseAvailability();
  const org = useOrg(privateAccess);
  const hasOrg = privateAccess && Boolean(org.data);
  const connectors = useConnectors(hasOrg);
  const policies = usePolicies(hasOrg);
  const input: ChecklistInput = {
    privateAccess,
    org: org.data ?? null,
    connectors: connectors.data ?? [],
    policies: policies.data ?? [],
    overview,
    analytics: Boolean(capabilities.sase?.modules.analytics),
  };
  const loading =
    overviewLoading ||
    (privateAccess && (org.isLoading || (hasOrg && (connectors.isLoading || policies.isLoading))));
  return { items: buildChecklist(input), loading };
}

/** Get started, step 2: how the organisation's people sign in. */
export function useSignInState(enabled: boolean) {
  return useSignIn(enabled);
}

/** The Agent gateways card: the customer's gateways, read through their own
 *  module's API and cache key (the same data as Compute → Agent gateways). */
export function useGatewayCount(enabled: boolean): { total: number; active: number } | null {
  const gateways = useQuery({
    queryKey: agentGatewayKeys.list(),
    queryFn: () => listGateways(),
    enabled,
  });
  if (!enabled || !gateways.data) return null;
  return {
    total: gateways.data.length,
    active: gateways.data.filter((g) => g.status === "active").length,
  };
}
